import { formatDate } from "@/lib/utils";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { DataTable } from "@/components/ui/data-table";
import {
  PlusCircle,
  Tag,
  Edit,
  Trash2,
  Search,
  MoreVertical,
  Image as ImageIcon,
} from "lucide-react";
import { API_ENDPOINTS } from "../../api/header";
import apiClient from "../../api/apiClient";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import { Checkbox } from "@/components/ui/checkbox";
import Modal from "@/components/ui/modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import MediaLibraryComponent from "../media/MediaLibraryComponent";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { PageContainer } from "@/components/layout/PageContainer";
import RecordNotFound from "@/core/components/RecordNotFound";

const BrandList = () => {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    description: "",
    logo: "",
    banner: "",
  });

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    description: "",
    onConfirm: () => {},
  });

  // Queries
  const { data: brandData, isLoading } = useQuery({
    queryKey: ["brands", currentPage, pageSize, searchText],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.BRANDS, {
        params: {
          page: currentPage,
          per_page: pageSize,
          search: searchText || undefined,
        },
      });
      return res.data;
    },
  });

  const brands = brandData?.data || [];
  const paginationMeta = {
    current_page: brandData?.meta?.current_page || brandData?.current_page || 1,
    last_page: brandData?.meta?.last_page || brandData?.last_page || 1,
    per_page: brandData?.meta?.per_page || brandData?.per_page || 15,
    total: brandData?.meta?.total || brandData?.total || 0,
  };

  // Mutations
  const saveBrandMutation = useMutation({
    mutationFn: async (apiData) => {
      const url = apiData.id
        ? API_ENDPOINTS.INVENTORY.BRAND_DETAILS(apiData.id)
        : API_ENDPOINTS.INVENTORY.BRANDS;
      const method = apiData.id ? "put" : "post";
      return await apiClient({ method, url, data: apiData });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      toast.success("Brand saved successfully");
      setModalOpen(false);
      setFormData({
        id: null,
        name: "",
        description: "",
        logo: "",
        banner: "",
      });
      setSelectedImage(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to save brand");
    },
  });

  const deleteBrandMutation = useMutation({
    mutationFn: async (id) => {
      return await apiClient.delete(API_ENDPOINTS.INVENTORY.BRAND_DETAILS(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      toast.success("Brand deleted successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete brand");
    },
  });

  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (perm) => {
    return permissions && permissions.includes(perm);
  };

  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
  const [mediaTarget, setMediaTarget] = useState("logo"); // "logo" or "banner"

  const [selectedImage, setSelectedImage] = useState(null);

  const fetchMediaDetails = async (mediaId) => {
    try {
      const res = await apiClient.get(
        API_ENDPOINTS.MEDIA.MEDIA_DETAILS(mediaId),
      );
      if (res.status === 200) {
        const mediaData = res.data.data;
        setFormData((prev) => ({
          ...prev,
          [mediaTarget]: mediaData.url,
        }));
      }
    } catch (error) {
      console.log("Failed to fetch media details", error);
    }
  };

  const handleMediaSelect = (selectedId) => {
    setSelectedImage(selectedId);
    fetchMediaDetails(selectedId);
  };

  const openMediaLibrary = (target) => {
    setMediaTarget(target);
    setIsMediaLibraryOpen(true);
  };

  const handleAdd = () => {
    setFormData({
      id: null,
      name: "",
      description: "",
      logo: "",
      banner: "",
    });
    setSelectedImage(null);
    setModalOpen(true);
  };

  // Reset page when search change
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentPage(1);
  }, [searchText]);

  const handleSave = async () => {
    saveBrandMutation.mutate(formData);
  };

  const handleEdit = async (brand) => {
    setFormData({ ...brand });
    // Reset media selection states when editing
    setSelectedImage(null);
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: "Are you sure?",
      description: "You won't be able to revert this!",
      onConfirm: () => {
        deleteBrandMutation.mutate(id);
      },
    });
  };

  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: "Brand",
      cell: ({ row }) => {
        const record = row.original;
        return (
          <div className="flex items-center">
            <img
              src={record.logo || "assets/img/product/noimage.png"}
              alt="Brand"
              className="mr-2 h-8 w-8 rounded object-cover border"
            />
            <span className="font-medium">{record.name}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ getValue }) => <span>{getValue() || "-"}</span>,
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {hasPermission("brand update") && (
              <DropdownMenuItem onClick={() => handleEdit(row.original)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
            )}
            {hasPermission("brand delete") && (
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => handleDelete(row.original.id)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  if (!hasPermission("brand read")) {
    return <RecordNotFound />;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Brands"
          subtitle="Manage your product brands"
        />
        <PageHeaderActions>
          {hasPermission("brand create") && (
            <Button onClick={handleAdd}>
              <PlusCircle className="mr-2 h-4 w-4" /> Add New Brand
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search brands..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={brands}
          isLoading={isLoading}
          enableRowSelection={true}
          pageCount={paginationMeta.last_page}
          footerInfo={
            <span className="font-medium text-foreground">
              Total Brands: {paginationMeta.total || 0}
            </span>
          }
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          onPaginationChange={(updater) => {
            const newState =
              typeof updater === "function"
                ? updater({
                    pageIndex: currentPage - 1,
                    pageSize: pageSize,
                  })
                : updater;
            setCurrentPage(newState.pageIndex + 1);
            setPageSize(newState.pageSize);
          }}
        />
      </TableCard>

      <Modal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={formData.id ? "Edit Brand" : "Add New Brand"}
        footer={
          <div className="flex justify-end gap-3 -mx-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saveBrandMutation.isPending}>
              {saveBrandMutation.isPending ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" /> Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        }
      >
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label>
              Brand Name <span className="text-destructive">*</span>
            </Label>
            <Input
              placeholder="Enter brand name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="Enter brand description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Logo</Label>
            <div
              className="border-2 border-dashed rounded-md p-4 text-center cursor-pointer hover:bg-muted transition-colors"
              onClick={() => openMediaLibrary("logo")}
            >
              {formData.logo ? (
                <div className="relative inline-block">
                  <img
                    src={formData.logo}
                    alt="Selected Logo"
                    className="h-24 w-24 object-cover rounded-md border"
                  />
                  <Button
                    size="icon"
                    variant="destructive"
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormData({ ...formData, logo: "" });
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center text-muted-foreground">
                  <ImageIcon className="h-8 w-8 mb-2" />
                  <span>Select Logo</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Confirmation Dialog */}
      <Modal
        open={confirmDialog.isOpen}
        onOpenChange={(open) =>
          setConfirmDialog({ ...confirmDialog, isOpen: open })
        }
        title={confirmDialog.title}
        footer={
          <div className="flex justify-end gap-3 -mx-2">
            <Button
              variant="outline"
              onClick={() =>
                setConfirmDialog({ ...confirmDialog, isOpen: false })
              }
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                confirmDialog.onConfirm();
                setConfirmDialog({ ...confirmDialog, isOpen: false });
              }}
            >
              Delete
            </Button>
          </div>
        }
      >
        <div className="py-4">
          <p className="text-sm text-muted-foreground font-medium">
            {confirmDialog.description}
          </p>
        </div>
      </Modal>

      <MediaLibraryComponent
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onSelect={handleMediaSelect}
        isMultiple={false}
        selectedIds={selectedImage ? [selectedImage] : []}
      />
    </PageContainer>
  );
};

export default BrandList;

